
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";
import { EmployeesList } from "@/components/admin/employees/list/EmployeesList";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Employee, ExtendedRole } from "@/components/admin/employees/types";
import { AppRole } from "@/types/database/auth";

export default function EmployeesPage() {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          navigate("/auth");
          return;
        }

        // Check if user is an admin
        const { data: roleData, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .single();

        if (error) {
          console.error("Role check error:", error);
        }

        if (roleData) {
          setIsAdmin(true);
        } else {
          // Allow producers to view the page but with limited functionality
          const { data: producerData, error: producerError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'producer')
            .single();
            
          if (producerError || !producerData) {
            toast({
              variant: "destructive",
              title: "Access Denied",
              description: "You don't have permission to access this page."
            });
            navigate("/dashboard");
            return;
          }
        }
        
        // Fetch employees with admin or producer roles directly from the view
        const { data: employeesData, error: employeesError } = await supabase
          .from('user_role_view')
          .select('*')
          .in('role', ['admin', 'producer'])
          .order('last_name', { ascending: true });
          
        if (employeesError) {
          console.error("Error fetching employees:", employeesError);
          setLoading(false);
          return;
        }
        
        if (!employeesData || employeesData.length === 0) {
          console.log("No employees found with admin or producer roles");
          setEmployees([]);
          setLoading(false);
          return;
        }
          
        // Transform to match Employee type
        const formattedEmployees: Employee[] = employeesData.map(profile => {
          return {
            id: profile.user_id,
            first_name: profile.first_name,
            last_name: profile.last_name,
            email: profile.email,
            phone: profile.phone || '',
            street: profile.street,
            city: profile.city,
            state: profile.state,
            zip_code: profile.zip_code,
            role: profile.role as ExtendedRole
          };
        });
        
        setEmployees(formattedEmployees);
        console.log("Found employees:", formattedEmployees.length);
        
        setLoading(false);
      } catch (error) {
        console.error("Error checking access:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again."
        });
        navigate("/dashboard");
      }
    };

    checkAccess();
  }, [navigate, toast]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Employee Management">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Employees</h2>
          {isAdmin && (
            <Button 
              onClick={() => navigate('/admin/employees/new')}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Employee
            </Button>
          )}
        </div>
        <EmployeesList employees={employees} />
      </div>
    </AdminLayout>
  );
}
